import { useNavigate } from "@tanstack/react-router";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState, type ReactElement, type RefObject } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  cn,
} from "@ndd/ui";
import { appointmentRdSearch, type AppointmentView } from "@/lib/appointments";
import {
  OWN_APPOINTMENTS_VALUE,
  appointmentScopeTitle,
  type AppointmentScope,
} from "@/lib/appointment-scope-state";
import { matchesNameSearch } from "@/lib/name-search";
import { useRdOptions } from "@/lib/use-rd-options";
import { PAGE_TITLE } from "./agenda-look";

function checkClass(selected: boolean): string {
  return cn("h-4 w-4", selected ? "opacity-100" : "opacity-0");
}

/**
 * The Appointment Home title as the RD picker (NO-467), the same shape as the
 * /rds roster title: the h1 IS the trigger, so whose schedule is on screen and
 * how to change it are one control, visible at every width without a slot in
 * the shell chrome. Modelled on RosterScopeSelector and the sidebar RdSwitcher
 * (Popover + Command, in-memory filtering). The button sits inside the h1 so
 * the heading stays valid HTML and keeps its level for assistive tech.
 *
 * Selecting navigates rather than setting state: the selection is `?rdId=`,
 * so the previous RD's rows unmount by routing. "My Appointments" is offered
 * only to a viewer who also holds their own rows (a dual rd+admin), and it
 * clears the selection rather than naming the viewer as an RD.
 */
export function AppointmentScopeSelector({
  scope,
  rdName,
  holdsOwnRows,
  headingRef,
  view,
  onSelectRd,
  ownLabel = "My Appointments",
}: {
  /** The page's view: Availability stays on Availability across an RD switch. */
  view?: AppointmentView;
  /**
   * Another page's own selection handler (Messages, NO-925). Absent, the
   * picker navigates to /appointments with `?rdId=`.
   */
  onSelectRd?: (rdId: string | null) => void;
  /** The picker's name for the viewer's own rows ("My Messages" on Messages). */
  ownLabel?: string;
  scope: AppointmentScope;
  /** The selected RD's name, verbatim; null until the roster resolves. */
  rdName: string | null;
  holdsOwnRows: boolean;
  headingRef?: RefObject<HTMLHeadingElement | null>;
}): ReactElement {
  const navigate = useNavigate();
  const options = useRdOptions();
  const [open, setOpen] = useState(false);
  // Null while a selected name is unresolved: a skeleton, never a greeting or
  // a guess, over a schedule that is not the viewer's. The skeleton is for
  // "not yet" only: once the roster has answered — with an error, or without
  // this id — the title falls back to a neutral label so a stale link cannot
  // leave a placeholder on screen forever.
  const rosterAnswered = options.isError || options.isSuccess;
  const resolvedTitle =
    scope.kind === "own" ? ownLabel : appointmentScopeTitle(scope, rdName);
  const title = resolvedTitle ?? (rosterAnswered ? "Selected RD" : null);

  const select = (rdId: string | null): void => {
    setOpen(false);

    if (onSelectRd) {
      onSelectRd(rdId);

      return;
    }

    void navigate({ to: "/appointments", search: appointmentRdSearch(rdId, view) });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <ScopeTitle
        title={title}
        rdName={rdName}
        unselected={scope.kind === "none"}
        headingRef={headingRef}
      />
      <PopoverContent
        align="start"
        className="min-w-[280px] w-[var(--radix-popover-trigger-width)] p-0"
      >
        <ScopeOptions
          scope={scope}
          rds={options.data?.data ?? []}
          unavailable={options.isError}
          holdsOwnRows={holdsOwnRows}
          ownLabel={ownLabel}
          onSelect={select}
        />
      </PopoverContent>
    </Popover>
  );
}

/** The h1 that is the picker's trigger. */
function ScopeTitle({
  title,
  rdName,
  unselected,
  headingRef,
}: {
  title: string | null;
  rdName: string | null;
  unselected: boolean;
  headingRef?: RefObject<HTMLHeadingElement | null>;
}): ReactElement {
  return (
    <h1
      ref={headingRef}
      tabIndex={-1}
      className={cn(
        "min-w-0 font-sans text-3xl font-bold tracking-tight text-foreground outline-none",
        PAGE_TITLE
      )}
    >
      <PopoverTrigger asChild>
        {/* Radix's PopoverTrigger injects aria-expanded/aria-controls at
            runtime; declaring role="combobox" statically trips a11y lint
            without them. */}
        <button
          type="button"
          aria-label={`${title ?? "Loading the selected RD"} — choose an RD`}
          className="inline-flex max-w-full items-center gap-2 rounded-md text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {title === null ? (
            <Skeleton aria-hidden="true" className="h-8 w-56" />
          ) : (
            <span
              // Long and non-Western names clip visually; the title carries them whole.
              title={rdName ?? undefined}
              className={cn("truncate", unselected && "text-subtle")}
            >
              {title}
            </span>
          )}
          <ChevronsUpDown aria-hidden="true" className="size-5 shrink-0 text-subtle" />
        </button>
      </PopoverTrigger>
    </h1>
  );
}

/** The picker's list: the viewer's own schedule when they hold one, then the RDs. */
function ScopeOptions({
  scope,
  rds,
  unavailable,
  holdsOwnRows,
  ownLabel,
  onSelect,
}: {
  scope: AppointmentScope;
  rds: ReadonlyArray<{ id: string; name: string }>;
  unavailable: boolean;
  holdsOwnRows: boolean;
  ownLabel: string;
  onSelect: (rdId: string | null) => void;
}): ReactElement {
  const selectedValue =
    scope.kind === "rd"
      ? scope.rdId
      : scope.kind === "own"
        ? OWN_APPOINTMENTS_VALUE
        : null;

  return (
    <Command filter={(_value, search, keywords) => matchesNameSearch(search, keywords)}>
      <CommandInput placeholder="Find an RD…" />
      <CommandList>
        <CommandEmpty>
          {unavailable ? "RDs are unavailable." : "No RD found."}
        </CommandEmpty>
        {/* cmdk keys highlight and selection by `value`, so every row needs
            its own: the reserved word for the viewer's own schedule and the
            RD id otherwise. The filter reads only `keywords`, so typing
            matches the name and never the id. */}
        {holdsOwnRows ? (
          <OwnScheduleOption
            label={ownLabel}
            selected={scope.kind === "own"}
            onSelect={onSelect}
          />
        ) : null}
        <CommandGroup heading="RDs">
          {rds.map((rd) => (
            <CommandItem
              key={rd.id}
              value={rd.id}
              keywords={[rd.name]}
              onSelect={() => onSelect(rd.id)}
            >
              <Check className={checkClass(rd.id === selectedValue)} />
              {/* Verbatim: this label names someone other than the viewer. */}
              <span className="flex-1 truncate">{rd.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

/** "My Appointments": offered only to a viewer who also holds their own rows. */
function OwnScheduleOption({
  label,
  selected,
  onSelect,
}: {
  label: string;
  selected: boolean;
  onSelect: (rdId: string | null) => void;
}): ReactElement {
  return (
    <>
      <CommandGroup>
        <CommandItem
          value={OWN_APPOINTMENTS_VALUE}
          keywords={[label]}
          onSelect={() => onSelect(null)}
        >
          <Check className={checkClass(selected)} />
          <span className="flex-1 truncate">{label}</span>
        </CommandItem>
      </CommandGroup>
      <CommandSeparator />
    </>
  );
}
